import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { ContactCollection, StatsLeadManagement } from './ContactCollection'
import Tasks from './tasks/Tasks'
import { useGetTaskStatsQuery } from '../../../../services/cellcast.v2'
import { TotalLeads } from './hooks'
import { useI18n } from '../../../Utils/_helpers/useI18n'

export const CONTACT_COLLECTION_CATEGORIES = {
    LEAD_MANAGEMENT: 'lead-management',
    TASKS: 'tasks',
} as const

type ContactCollectionCategoryValue =
    (typeof CONTACT_COLLECTION_CATEGORIES)[keyof typeof CONTACT_COLLECTION_CATEGORIES]

const CONTACT_COLLECTION_TABS: Array<{
    label: string
    value: ContactCollectionCategoryValue
    disabled: boolean
}> = [
        { label: 'Lead Management', value: CONTACT_COLLECTION_CATEGORIES.LEAD_MANAGEMENT, disabled: false },
        { label: 'Tasks', value: CONTACT_COLLECTION_CATEGORIES.TASKS, disabled: false },
    ]

type TotalState = Record<ContactCollectionCategoryValue, number>

const DEFAULT_CATEGORY = CONTACT_COLLECTION_CATEGORIES.LEAD_MANAGEMENT

const TabContactCollection = () => {
    const { t } = useI18n()
    const [searchParams, setSearchParams] = useSearchParams()
    const paramCategory = searchParams.get('category')

    const initialCategory = CONTACT_COLLECTION_TABS.some((tab) => tab.value === paramCategory)
        ? (paramCategory as ContactCollectionCategoryValue)
        : DEFAULT_CATEGORY

    const [category, setCategory] =
        useState<ContactCollectionCategoryValue>(initialCategory)

    const [leadTotal, setLeadTotal] = useState<TotalLeads | null>(null)
    const [tabCounts, setTabCounts] = useState<TotalState>({
        [CONTACT_COLLECTION_CATEGORIES.LEAD_MANAGEMENT]: 0,
        [CONTACT_COLLECTION_CATEGORIES.TASKS]: 0,
    })

    const { data: taskStatsData } = useGetTaskStatsQuery(undefined, {
        refetchOnMountOrArgChange: true,
    })

    // Mobile detection for stats
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768)
        }
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    useEffect(() => {
        const currentParams = new URLSearchParams(searchParams)
        currentParams.set('category', category)
        setSearchParams(currentParams, { replace: true })
    }, [category, setSearchParams, searchParams])

    useEffect(() => {
        const nextTaskCount = taskStatsData?.data?.totalTasks
        setTabCounts((prev) => ({
            ...prev,
            [CONTACT_COLLECTION_CATEGORIES.TASKS]:
                typeof nextTaskCount === 'number' ? nextTaskCount : 0,
        }))
    }, [taskStatsData?.data?.totalTasks])

    useEffect(() => {
        if (leadTotal) {
            setTabCounts((prev) => ({
                ...prev,
                [CONTACT_COLLECTION_CATEGORIES.LEAD_MANAGEMENT]: leadTotal.total || 0,
            }))
        }
    }, [leadTotal])

    return (
        <div className='contact-collection-container' data-page='contact-collection'>
            {leadTotal && <StatsLeadManagement total={leadTotal} isMobile={isMobile} />}
            <div className='card'>
                <div className='card-header border-0 pt-12 px-14'>
                    <h3 className='card-title align-items-start flex-column'>
                        <span className='card-label fw-bolder fs-2'>{t('RAW_CONTACT_COLLECTION.TITLE')}</span>
                        <span className='text-muted fw-bold fs-7'>
                            {t('RAW_CONTACT_COLLECTION.DESCRIPTION')}
                        </span>
                    </h3>
                </div>

                <div className='d-flex overflow-auto h-35px px-8 mt-8 justify-content-between border-bottom mb-2'>
                    <ul
                        className='nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-3 flex-nowrap'
                        style={{ fontWeight: 500 }}
                    >
                        {CONTACT_COLLECTION_TABS.map((tab) => {
                            const isActive = tab.value === category
                            const isDisabled = tab.disabled
                            const count = tabCounts[tab.value]

                            return (
                                <li
                                    key={tab.value}
                                    className='nav-item'
                                    onClick={() => !isDisabled && setCategory(tab.value)}
                                >
                                    <div
                                        className={`nav-link me-6 text-active-primary fs-3 ${isActive ? 'active' : ''
                                            } ${isDisabled ? 'pe-none text-muted opacity-50' : 'cursor-pointer'}`}
                                    >
                                        {tab.label} {count ? `(${count})` : ''}
                                    </div>
                                </li>
                            )
                        })}
                    </ul>
                </div>

                {category === CONTACT_COLLECTION_CATEGORIES.LEAD_MANAGEMENT ? (
                    <ContactCollection hideStats embedded onTotalChange={setLeadTotal} />
                ) : null}
                {category === CONTACT_COLLECTION_CATEGORIES.TASKS ? <Tasks /> : null}
            </div>
        </div>
    )
}

export default TabContactCollection

